import React from 'react';
import { MessageCircle, FileText, Mail, Phone } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { preloadRoute } from '@/hooks/useRoutePreload';

const ResourcesMegaMenu: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const bgColor = isDark ? '#1a1a1a' : '#f4f2f1';
  const hoverBg = isDark ? '#2a2a2a' : '#F5F5F2';
  const borderColor = isDark ? '#333333' : '#E5E5E5';
  const labelColor = isDark ? '#888888' : '#888888';
  const titleColor = isDark ? '#f4f2f1' : '#111111';
  const descColor = isDark ? '#999999' : '#555555';
  const iconColor = isDark ? '#888888' : '#888888';
  const gridLineColor = isDark ? '#444444' : '#d0d0d0';
  const sidebarBg = isDark ? '#222222' : '#FFFFFF';

  const iconBoxStyle: React.CSSProperties = {
    backgroundImage: `linear-gradient(to right, ${gridLineColor} 1px, transparent 1px), linear-gradient(to bottom, ${gridLineColor} 1px, transparent 1px)`,
    backgroundSize: '5px 5px',
    WebkitMaskImage: 'radial-gradient(circle, black 0%, transparent 70%)',
    maskImage: 'radial-gradient(circle, black 0%, transparent 70%)',
  };

  return (
    <div className="w-[660px] flex" style={{ backgroundColor: bgColor }}>
      {/* Free Marketing Image Card */}
      <a 
        href="/free-marketing" 
        className="relative w-[180px] flex-shrink-0 overflow-hidden group"
        onMouseEnter={() => preloadRoute('/free-marketing')}
      >
        <img 
          src="/lovable-uploads/aeo-book-cover.jpg" 
          alt="Free Marketing Resources" 
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <span className="text-[10px] font-medium uppercase tracking-wider text-white/70">Learn</span>
          <h4 className="text-sm font-semibold text-white mt-1">Free Marketing</h4>
          <p className="text-[11px] text-white/80 mt-0.5">Expert tips & tactics</p>
        </div>
      </a>

      {/* Support Category */}
      <div className="w-[240px] p-6">
        <h3 className="text-[11px] font-medium uppercase tracking-wider mb-3" style={{ color: labelColor }}>
          Support
        </h3>
        <div className="space-y-2">
          <a 
            href="/help" 
            className="group flex items-start gap-3 p-2 rounded-md transition-colors" 
            style={{ backgroundColor: 'transparent' }} 
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; preloadRoute('/help'); }} 
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={iconBoxStyle}>
              <MessageCircle className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
            </div>
            <div>
              <h4 className="text-sm font-medium" style={{ color: titleColor }}>Help Center</h4>
              <p className="text-xs mt-0.5" style={{ color: descColor }}>Find instant answers</p>
            </div>
          </a>
          <a 
            href="/blog" 
            className="group flex items-start gap-3 p-2 rounded-md transition-colors" 
            style={{ backgroundColor: 'transparent' }} 
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; preloadRoute('/blog'); }} 
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={iconBoxStyle}>
              <FileText className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
            </div>
            <div>
              <h4 className="text-sm font-medium" style={{ color: titleColor }}>Articles</h4>
              <p className="text-xs mt-0.5" style={{ color: descColor }}>Latest insights & updates</p>
            </div>
          </a>
        </div>
      </div>

      {/* Sidebar - Get in Touch */}
      <div className="w-[240px] p-6 border-l" style={{ borderColor, backgroundColor: sidebarBg }}>
        <h3 className="text-[11px] font-medium uppercase tracking-wider mb-5" style={{ color: labelColor }}>
          Get in Touch
        </h3>
        <div className="space-y-4">
           <a 
             href="mailto:hello@storymarketing.group" 
             className="group flex items-start gap-3 transition-colors hover:underline" 
             style={{ color: descColor }}
           >
             <Mail className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: iconColor }} strokeWidth={1.5} />
             <span className="text-xs leading-relaxed">hello@storymarketing.group</span>
           </a>
        </div>
      </div>
    </div>
  );
};

export default ResourcesMegaMenu;
